// app/frontend/components/community/PostContent.tsx
import { useState, useEffect, useCallback } from "react";
import { Box, Card, Group, Stack, Image, Loader, ActionIcon } from "@mantine/core";
import { IconPlayerPlay, IconExternalLink } from "@tabler/icons-react";
import { L2KText, L2KCaption } from "../ui/Typography";

// Link Preview types
interface LinkPreview {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  site_name: string | null;
  domain: string | null;
  is_video: boolean;
  loading?: boolean;
  error?: boolean;
}

// URL detection regex
const URL_REGEX = /https?:\/\/[^\s<>"\[\]]+/gi;

// Link Preview Card Component
function LinkPreviewCard({ preview }: { preview: LinkPreview }) {
  if (preview.loading) {
    return (
      <Card
        padding="sm"
        radius="md"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Group gap="sm" align="center">
          <Loader size="sm" color="orange" />
          <L2KCaption variant="tertiary">Loading preview...</L2KCaption>
        </Group>
      </Card>
    );
  }

  if (preview.error || (!preview.title && !preview.image)) {
    return null;
  }

  return (
    <Card
      component="a"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      padding={0}
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        overflow: "hidden",
        textDecoration: "none",
        display: "block",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.3)";
        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)";
      }}
    >
      <Group gap={0} wrap="nowrap" align="stretch">
        {/* 16:9 Thumbnail (160x90) */}
        {preview.image && (
          <Box
            style={{
              position: "relative",
              width: 160,
              height: 90,
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            <Image
              src={preview.image}
              alt={preview.title || "Link preview"}
              width={160}
              height={90}
              fit="cover"
            />
            {preview.is_video && (
              <Box
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconPlayerPlay size={16} color="#fff" style={{ marginLeft: 2 }} />
              </Box>
            )}
          </Box>
        )}

        {/* Content */}
        <Box p="sm" style={{ flex: 1, minWidth: 0 }}>
          <Stack gap={4}>
            <Group gap={4} align="center">
              <L2KCaption variant="tertiary" style={{ fontSize: 10 }}>
                {preview.site_name || preview.domain}
              </L2KCaption>
              <IconExternalLink size={10} color="#737373" />
            </Group>
            {preview.title && (
              <L2KText size="xs" style={{ fontWeight: 500, lineHeight: 1.3 }}>
                {preview.title.length > 60 ? preview.title.slice(0, 60) + "..." : preview.title}
              </L2KText>
            )}
            {preview.description && (
              <L2KCaption variant="tertiary" style={{
                fontSize: 11,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}>
                {preview.description}
              </L2KCaption>
            )}
          </Stack>
        </Box>
      </Group>
    </Card>
  );
}

interface PostContentProps {
  body: string;
  size?: "xs" | "sm" | "md";
  maxPreviews?: number; // Limit number of previews shown (for list views)
  truncateText?: boolean; // Truncate body text
}

export function PostContent({ body, size = "sm", maxPreviews, truncateText }: PostContentProps) {
  const [linkPreviews, setLinkPreviews] = useState<LinkPreview[]>([]);
  const [fetchedUrls, setFetchedUrls] = useState<Set<string>>(new Set());

  // Fetch link preview from API
  const fetchLinkPreview = useCallback(async (url: string) => {
    if (fetchedUrls.has(url)) return;

    // Add loading state
    setLinkPreviews((prev) => [...prev, { url, loading: true } as LinkPreview]);
    setFetchedUrls((prev) => new Set(prev).add(url));

    try {
      const response = await fetch(`/api/link_preview?url=${encodeURIComponent(url)}`);
      if (response.ok) {
        const data = await response.json();
        setLinkPreviews((prev) =>
          prev.map((p) => (p.url === url ? { ...data, loading: false } : p))
        );
      } else {
        // Mark as error
        setLinkPreviews((prev) =>
          prev.map((p) => (p.url === url ? { ...p, loading: false, error: true } : p))
        );
      }
    } catch {
      // Mark as error
      setLinkPreviews((prev) =>
        prev.map((p) => (p.url === url ? { ...p, loading: false, error: true } : p))
      );
    }
  }, [fetchedUrls]);

  // Detect URLs in body and fetch previews
  useEffect(() => {
    const urls = body.match(URL_REGEX) || [];
    const uniqueUrls = [...new Set(urls)];

    uniqueUrls.forEach((url) => {
      if (!fetchedUrls.has(url)) {
        fetchLinkPreview(url);
      }
    });
  }, [body, fetchLinkPreview, fetchedUrls]);

  // Render body text with URLs as clickable links
  const renderBodyWithLinks = () => {
    const parts = body.split(URL_REGEX);
    const urls = body.match(URL_REGEX) || [];

    const elements: React.ReactNode[] = [];

    parts.forEach((part, index) => {
      if (part) {
        elements.push(<span key={`text-${index}`}>{part}</span>);
      }
      if (urls[index]) {
        elements.push(
          <a
            key={`url-${index}`}
            href={urls[index]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#FF9800", textDecoration: "none" }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
          >
            {urls[index]}
          </a>
        );
      }
    });

    return elements;
  };

  const validPreviews = linkPreviews.filter((p) => !p.error && (p.title || p.image || p.loading));
  const displayPreviews = maxPreviews ? validPreviews.slice(0, maxPreviews) : validPreviews;

  const textStyle: React.CSSProperties = {
    whiteSpace: "pre-wrap",
    lineHeight: 1.7,
    ...(truncateText && {
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical" as const,
      overflow: "hidden",
    }),
  };

  return (
    <Stack gap="sm">
      <L2KText size={size} variant="secondary" style={textStyle}>
        {renderBodyWithLinks()}
      </L2KText>

      {displayPreviews.length > 0 && (
        <Stack gap="xs">
          {displayPreviews.map((preview) => (
            <LinkPreviewCard key={preview.url} preview={preview} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
